import { IconAlertTriangle, IconPlayerPause } from "@tabler/icons-react";
import { Outlet } from "react-router-dom";

import { useSession } from "../../auth/SessionProvider";
import { DEV_MODE_USERNAME } from "../../config";
import { UploadStrip } from "../upload/UploadStrip";
import { Sidebar } from "./Sidebar";

/** Banner shown whenever the server accepts an unauthenticated header. */
function DevModeBanner(): JSX.Element {
  return (
    <p className="banner banner--dev" role="status">
      <IconAlertTriangle size={14} aria-hidden="true" />
      Development mode, no authentication — the server trusts whatever name this
      page sends, and this page sends “{DEV_MODE_USERNAME}”. Never point this at
      a console holding a client's data.
    </p>
  );
}

/**
 * The emergency stop, said once, on every screen.
 *
 * Shown from `me.console_paused`, which is the server's own answer — somebody
 * who does not know the console is paused reads every refusal as a bug.
 */
function PausedBanner(): JSX.Element {
  return (
    <p className="banner banner--paused" role="status">
      <IconPlayerPause size={14} aria-hidden="true" />
      The console is paused: no new dataset, no new file and no new link right
      now. Nothing already stored is affected, and links already issued keep
      working until they expire.
    </p>
  );
}

/**
 * The console frame: a named sidebar on the left, the screen filling the rest.
 *
 * Returns:
 *     The application shell.
 */
export function AppShell(): JSX.Element {
  const { authConfig, me } = useSession();
  return (
    <div className="shell">
      <Sidebar />
      <div className="shell__main">
        {authConfig.mode === "dev" ? <DevModeBanner /> : null}
        {me.console_paused ? <PausedBanner /> : null}
        <UploadStrip />
        <main className="shell__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
